import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import UserSearchPicker, { OrgUser } from './UserSearchPicker';

interface CreateConversationDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    type: 'ai_chat' | 'group' | 'channel';
    title?: string;
    slug?: string;
    description?: string;
    isPublic?: boolean;
    historyVisible?: boolean;
    participantIds?: string[];
  }) => void;
}

const CreateConversationDialog: React.FC<CreateConversationDialogProps> = ({
  open,
  onClose,
  onCreate,
}) => {
  const [title, setTitle] = useState('');
  const [isChannel, setIsChannel] = useState(false);
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [historyVisible, setHistoryVisible] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState<OrgUser[]>([]);

  const handleCreate = () => {
    if (isChannel) {
      onCreate({
        type: 'channel',
        title: title || 'New Channel',
        slug: title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || undefined,
        description: description || undefined,
        isPublic,
      });
    } else if (selectedMembers.length > 0) {
      // Inviting members makes it a group conversation
      onCreate({
        type: 'group',
        title: title || undefined,
        participantIds: selectedMembers.map(m => m.id),
        historyVisible,
      });
    } else {
      // No members invited = standard AI chat
      onCreate({
        type: 'ai_chat',
        title: title || undefined,
      });
    }
    handleClose();
  };

  const handleClose = () => {
    setTitle('');
    setIsChannel(false);
    setDescription('');
    setIsPublic(true);
    setHistoryVisible(true);
    setSelectedMembers([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Conversation</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Name (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
          placeholder={isChannel ? 'general' : 'New Conversation'}
        />

        {!isChannel && (
          <div style={{ marginTop: 8 }}>
            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 8 }}>
              Invite members (optional)
            </Typography>
            <UserSearchPicker
              onSelectUsers={setSelectedMembers}
            />
            <Typography variant="caption" color="text.secondary" style={{ marginTop: 4, display: 'block' }}>
              AI teammates are always available. Leave empty for a private AI conversation.
            </Typography>
          </div>
        )}

        {!isChannel && selectedMembers.length > 0 && (
          <FormControlLabel
            control={
              <Switch
                checked={historyVisible}
                onChange={(e) => setHistoryVisible(e.target.checked)}
                size="small"
              />
            }
            label="New members can see full history"
            style={{ marginTop: 8 }}
          />
        )}

        <Divider style={{ marginTop: 16, marginBottom: 16 }} />

        <FormControlLabel
          control={
            <Switch
              checked={isChannel}
              onChange={(e) => {
                setIsChannel(e.target.checked);
                if (e.target.checked) {
                  setSelectedMembers([]);
                }
              }}
              size="small"
            />
          }
          label="Make this an org-wide channel"
        />

        {isChannel && (
          <>
            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              margin="normal"
              size="small"
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  size="small"
                />
              }
              label="Public (anyone in org can join)"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={isChannel && !title.trim()}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateConversationDialog;
